import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Clock, Database, Loader2, Search, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BrandCard, BrandCardContent } from "@/components/brand/brand-card";
import { BrandInput } from "@/components/brand/brand-input";
import { PublicHeader } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/admin/activity-logs")({
	component: AdminActivityLogsPage,
});

type ActivityLog = {
	id: string;
	action: string;
	actorEmail?: string;
	actorUserId?: string;
	createdAt: string | number;
	payload: string;
	resourceId?: string;
	resourceType: string;
	threadId?: string;
	workspaceId?: string;
};

type LogPayload = Record<string, unknown>;

function parsePayload(payload: string): LogPayload | string {
	try {
		const parsed = JSON.parse(payload);
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			return parsed as LogPayload;
		}
		return String(parsed);
	} catch {
		return payload;
	}
}

function tryParseJsonString(value: string): unknown {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function humanizeKey(key: string) {
	return key
		.replace(/([A-Z])/g, " $1")
		.replace(/[_-]/g, " ")
		.replace(/^./, (letter) => letter.toUpperCase());
}

function renderPrimitive(value: unknown) {
	if (value === null || value === undefined || value === "") {
		return <span className="text-muted-foreground">None</span>;
	}
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (typeof value === "number") return value;
	return String(value);
}

function DetailValue({ value }: { value: unknown }) {
	const normalized =
		typeof value === "string" ? tryParseJsonString(value) : value;

	if (
		normalized &&
		typeof normalized === "object" &&
		!Array.isArray(normalized)
	) {
		return (
			<div className="mt-1 space-y-1 rounded-md bg-background/70 p-3 ring-1 ring-border/60">
				{Object.entries(normalized as Record<string, unknown>).map(
					([key, childValue]) => (
						<div key={key} className="grid gap-1 sm:grid-cols-[9rem_1fr]">
							<div className="text-muted-foreground">{humanizeKey(key)}</div>
							<div className="min-w-0 whitespace-pre-wrap break-words">
								<DetailValue value={childValue} />
							</div>
						</div>
					),
				)}
			</div>
		);
	}

	if (Array.isArray(normalized)) {
		return (
			<div className="mt-1 space-y-2">
				{normalized.map((item) => (
					<div
						className="rounded-md bg-background/70 p-3 ring-1 ring-border/60"
						key={JSON.stringify(item).slice(0, 80)}
					>
						<DetailValue value={item} />
					</div>
				))}
			</div>
		);
	}

	return <>{renderPrimitive(normalized)}</>;
}

function DetailRow({ label, value }: { label: string; value: unknown }) {
	if (value === undefined || value === null || value === "") return null;
	return (
		<div className="grid gap-1 text-sm sm:grid-cols-[8rem_1fr]">
			<div className="font-medium text-muted-foreground">{label}</div>
			<div className="min-w-0 whitespace-pre-wrap break-words">
				<DetailValue value={value} />
			</div>
		</div>
	);
}

function MessageBody({ children }: { children: unknown }) {
	if (children === undefined || children === null || children === "")
		return null;
	return (
		<div className="mt-4 rounded-md bg-muted/60 p-4 text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
			{String(children)}
		</div>
	);
}

function ToolCalls({ value }: { value: unknown }) {
	if (!Array.isArray(value) || value.length === 0) return null;
	return (
		<div className="mt-4 space-y-3">
			{value.map((toolCall, index) => {
				const tool =
					toolCall && typeof toolCall === "object"
						? (toolCall as Record<string, unknown>)
						: {};
				return (
					<div
						className="rounded-md bg-muted/60 p-4 ring-1 ring-border/60"
						key={String(
							tool.id ?? tool.callId ?? JSON.stringify(tool).slice(0, 80),
						)}
					>
						<div className="mb-3 text-sm font-semibold">
							{String(tool.name ?? `Tool call ${index + 1}`)}
						</div>
						<div className="space-y-3">
							<DetailRow label="Arguments" value={tool.arguments} />
							<DetailRow label="Result" value={tool.result} />
							<DetailRow label="Error" value={tool.error} />
						</div>
					</div>
				);
			})}
		</div>
	);
}

function LogPayloadView({ log }: { log: ActivityLog }) {
	const payload = parsePayload(log.payload);

	if (typeof payload === "string") {
		return <MessageBody>{payload}</MessageBody>;
	}

	if (log.action === "assistant_message.user") {
		return (
			<>
				<MessageBody>{payload.content}</MessageBody>
				<div className="mt-3 space-y-2">
					<DetailRow label="Auth method" value={payload.authMethod} />
				</div>
			</>
		);
	}

	if (log.action === "assistant_message.assistant") {
		return <MessageBody>{payload.content}</MessageBody>;
	}

	if (log.action === "assistant_message.tool_request") {
		return (
			<>
				<MessageBody>{payload.content}</MessageBody>
				<ToolCalls value={payload.toolCalls} />
			</>
		);
	}

	if (log.action === "assistant_tool_calls.executed") {
		return <ToolCalls value={payload.toolCalls} />;
	}

	if (log.action === "assistant_tool_calls.pending_confirmation") {
		return <ToolCalls value={payload.toolCalls} />;
	}

	if (log.action.startsWith("mcp_tool.")) {
		return (
			<div className="mt-4 space-y-3 rounded-md bg-muted/60 p-4">
				<DetailRow label="Tool" value={payload.toolName} />
				<DetailRow label="Status" value={payload.status} />
				<DetailRow label="Duration" value={payload.durationMs} />
				<DetailRow label="Arguments" value={payload.arguments} />
				<DetailRow label="Result" value={payload.result} />
				<DetailRow label="Error" value={payload.error} />
			</div>
		);
	}

	if (log.action.startsWith("assistant_tool_call.")) {
		return (
			<div className="mt-4 space-y-3 rounded-md bg-muted/60 p-4">
				<DetailRow label="Tool" value={payload.name} />
				<DetailRow label="Status" value={payload.status} />
				<DetailRow label="Arguments" value={payload.arguments} />
				<DetailRow label="Result" value={payload.response} />
				<DetailRow label="Error" value={payload.error} />
				<DetailRow label="Summary" value={payload.summary} />
			</div>
		);
	}

	return (
		<div className="mt-4 space-y-2 rounded-md bg-muted/60 p-4">
			{Object.entries(payload).map(([key, value]) => (
				<DetailRow key={key} label={humanizeKey(key)} value={value} />
			))}
		</div>
	);
}

function formatDate(value: string | number) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString();
}

function AdminActivityLogsPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({ to: "/login", replace: true });
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { data: logs = [], isLoading } = useQuery({
		queryKey: ["admin-activity-logs"],
		queryFn: async () => {
			const res = await fetch("/api/admin/list-activity-logs", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load activity logs");
			const data = await res.json();
			return (data.logs ?? []) as ActivityLog[];
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	const sortedLogs = useMemo(() => {
		return logs.toSorted(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);
	}, [logs]);

	const filteredLogs = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return sortedLogs;
		return sortedLogs.filter((log) => {
			const haystack = [
				log.action,
				log.actorEmail,
				log.actorUserId,
				log.resourceType,
				log.resourceId,
				log.threadId,
				log.workspaceId,
				log.payload,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return haystack.includes(query);
		});
	}, [sortedLogs, search]);

	if (isPending || isLoading || !isAdmin) {
		return (
			<>
				<PublicHeader />
				<main className="min-h-screen flex items-center justify-center">
					<Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
				</main>
			</>
		);
	}

	return (
		<>
			<PublicHeader />
			<main className="max-w-6xl mx-auto px-4 py-8">
				<div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
					<div>
						<h1 className="text-3xl font-bold tracking-tight">Activity logs</h1>
						<p className="mt-2 text-muted-foreground">
							{logs.length} admin-only event{logs.length !== 1 ? "s" : ""}
						</p>
					</div>
					<div className="relative w-full md:w-80">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<BrandInput
							className="pl-9"
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search logs..."
							value={search}
						/>
					</div>
				</div>

				<div className="space-y-3">
					{filteredLogs.map((log) => (
						<BrandCard key={log.id}>
							<BrandCardContent className="p-5">
								<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
									<div className="min-w-0 flex-1">
										<div className="flex flex-wrap items-center gap-2">
											<span className="rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary ring-1 ring-primary/15">
												{log.action}
											</span>
											<span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
												{log.resourceType}
											</span>
										</div>
										<div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
											<span className="inline-flex items-center gap-1.5">
												<Clock className="h-3.5 w-3.5" />
												{formatDate(log.createdAt)}
											</span>
											<span className="inline-flex items-center gap-1.5">
												<User className="h-3.5 w-3.5" />
												{log.actorEmail ?? log.actorUserId ?? "System"}
											</span>
											{log.workspaceId && (
												<span className="inline-flex items-center gap-1.5">
													<Database className="h-3.5 w-3.5" />
													{log.workspaceId}
												</span>
											)}
										</div>
									</div>
									<div className="text-xs text-muted-foreground lg:text-right">
										{log.threadId && <div>Thread: {log.threadId}</div>}
										{log.resourceId && <div>Resource: {log.resourceId}</div>}
									</div>
								</div>
								<LogPayloadView log={log} />
							</BrandCardContent>
						</BrandCard>
					))}
					{filteredLogs.length === 0 && (
						<BrandCard>
							<BrandCardContent className="py-16 text-center">
								<p className="text-sm text-muted-foreground">
									{search
										? "No logs match your search"
										: "No activity logged yet"}
								</p>
							</BrandCardContent>
						</BrandCard>
					)}
				</div>
			</main>
		</>
	);
}
